import { format } from 'date-fns'
import {
  StatusGlyph,
  type StatusGlyphProps,
} from '@/features/jobs/components/StatusGlyph'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import styles from './JobHeading.module.css'

export interface JobHeadingProps {
  eyebrow: string
  title: string
  glyph: StatusGlyphProps['glyph']
  tone: StatusGlyphProps['tone']
  formattedStatus: string
  workspaceName?: string | null
  /**
   * When the pending job is allowed to advance, if it is pending.
   */
  advanceAllowedDate?: Date | null
  dateDeadline?: Date | null
  since?: string | null
  isDetailsOpen: boolean
  onDetailsToggle: () => unknown
}

/**
 * Who the job is for, with where it stands and when it is due on one line.
 */
export function JobHeading({
  eyebrow,
  title,
  glyph,
  tone,
  formattedStatus,
  workspaceName,
  advanceAllowedDate,
  dateDeadline,
  since,
  isDetailsOpen,
  onDetailsToggle,
}: JobHeadingProps) {
  return (
    <div className={styles.root}>
      <div className={styles.titles}>
        <div className={styles.eyebrow}>{eyebrow}</div>
        <h1 className={styles.title}>{title}</h1>
      </div>
      <div className={styles.facts}>
        <span className={styles.fact} data-test="job-header-status">
          <StatusGlyph glyph={glyph} tone={tone} />
          {formattedStatus}
        </span>
        {workspaceName && (
          <span className={styles.fact}>
            <span className={styles.workspaceMark}>
              {initials(workspaceName)}
            </span>
            {workspaceName}
          </span>
        )}
        {advanceAllowedDate && (
          <span className={styles.fact} data-test="job-header-advance-allowed">
            <Icon
              name="date-start"
              size={14}
              color="var(--text-tertiary)"
              aria-hidden
            />
            Allowed from {format(advanceAllowedDate, 'MMM d')}
          </span>
        )}
        {dateDeadline && (
          <span className={styles.fact}>
            <Icon
              name="date-deadline"
              size={14}
              color="var(--text-tertiary)"
              aria-hidden
            />
            Due {format(dateDeadline, 'MMM d')}
          </span>
        )}
        {since != null && (
          <span className={styles.fact}>
            <Icon
              name="clock"
              size={14}
              color="var(--text-tertiary)"
              aria-hidden
            />
            {since}
          </span>
        )}
        <RawButton
          className={styles.detailsToggle}
          aria-expanded={isDetailsOpen}
          onPress={onDetailsToggle}
        >
          <Icon name="panel" size={14} rotate={180} aria-hidden />
          {isDetailsOpen ? 'Hide details' : 'More details'}
        </RawButton>
      </div>
    </div>
  )
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('')
}
